import { IKeyObjective } from 'legacy/lut/classes/models/partner360/IFormOptionsJBP'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { FC, ReactNode, useCallback, useMemo } from 'react'

import { IChangeTreeSelectFn, TTypeOfSelectedNodes } from 'cng-common/components/treeSelect'
import { useFormField } from 'cng-common/features/form'

import { transformKeyObjectivesDataToHierarchyFormat } from '../../../../helpers/transformKeyObjectivesDataToHierarchyFormat'
import { KeyObjectivesTreeSelect } from '../keyObjectivesTreeSelect/KeyObjectivesTreeSelect'

interface IMappedKeyObjectiveItem {
  itemId: string
  parentId: string | null
  name: string
}

interface IKeyObjectivesTreeProps {
  keyObjectivesSettingsList: IKeyObjective[]
  formKey: string
  initialValue: string[]
  programType?: ProgramTypes
  rtm?: string
  label?: ReactNode
  className?: string
  isDataInsightTab?: boolean
  keyObjectivesFilter?: string
  typeOfSelectedNodes?: TTypeOfSelectedNodes
}

export const KeyObjectivesTree: FC<IKeyObjectivesTreeProps> = ({
  keyObjectivesSettingsList,
  formKey,
  initialValue,
  programType,
  rtm,
  label,
  className,
  isDataInsightTab,
  keyObjectivesFilter,
  typeOfSelectedNodes = 'displayedItems',
}) => {
  const keyObjectivesWithHierarchy = useMemo(() => {
    return transformKeyObjectivesDataToHierarchyFormat({
      keyObjectivesSettingsList,
      programType,
      keyObjectivesFilter,
      isDataInsightTab,
    })
  }, [keyObjectivesSettingsList, programType, keyObjectivesFilter, isDataInsightTab])

  const treeItems = useMemo<IMappedKeyObjectiveItem[]>(() => {
    return keyObjectivesWithHierarchy
      .filter((item) => !rtm || !item.rtm || item.rtm.includes(rtm))
      .map((item) => ({
        itemId: item.key,
        parentId: item.parent ?? null,
        name: item.key,
      }))
  }, [keyObjectivesWithHierarchy, rtm])

  const { value, handleChange } = useFormField<string[]>({
    defaultValue: initialValue,
    formKey,
    field: 'keyObjectiveTags',
  })

  const cleanValues = useMemo(() => value.map((val) => (val ?? '').trim()), [value])

  const handleTreeChange = useCallback<IChangeTreeSelectFn<IMappedKeyObjectiveItem>>(
    (displayedItems, allSelectedItems, onlyLeaves) => {
      switch (typeOfSelectedNodes) {
        case 'onlyLeavesItems':
          handleChange(onlyLeaves.map((item) => item.itemId))
          break

        case 'displayedItems':
          handleChange(displayedItems.map((item) => item.itemId))
          break

        case 'allSelectedItems':
        default:
          handleChange(allSelectedItems.map((item) => item.itemId))
          break
      }
    },
    [handleChange, typeOfSelectedNodes]
  )

  const handleSearch = useCallback((item: IMappedKeyObjectiveItem, searchValue: string) => {
    return item.name.toLowerCase().includes(searchValue.toLowerCase())
  }, [])

  return (
    <div className={className}>
      <KeyObjectivesTreeSelect
        values={cleanValues || []}
        data={treeItems}
        itemCaption="name"
        label={label}
        onChange={handleTreeChange}
        onSearch={handleSearch}
        typeOfSelectedNodes={typeOfSelectedNodes}
      />
    </div>
  )
}
